import { VisibilityIcon, VisibilityOffIcon } from "@bvhi/ui-icons";
import { InputBaseComponentProps, TextField as MuiTextField, TextFieldProps } from "@mui/material";
import React, { ChangeEvent, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { IconButton } from "./IconButton";
import { InputAdornment } from "./InputAdornment";

type Props = object & TextFieldProps;

export const TextField = ({ children, InputLabelProps, ...props }: Props) => {
  return (
    <MuiTextField
      InputLabelProps={{
        shrink: true,
        ...InputLabelProps,
      }}
      {...props}
    >
      {children}
    </MuiTextField>
  );
};

type PasswordProps = object & Omit<TextFieldProps, "type" | "endAdornment">;

export const HiddenTextField = ({ children, InputLabelProps, InputProps, ...props }: PasswordProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <MuiTextField
      type={showPassword ? "text" : "password"}
      InputLabelProps={{
        shrink: true,
        ...InputLabelProps,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              edge="end"
              onClick={() => setShowPassword((prev) => !prev)}
              onMouseDown={handleMouseDownPassword}
              onMouseUp={handleMouseUpPassword}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </InputAdornment>
        ),
        ...InputProps,
      }}
      {...props}
    >
      {children}
    </MuiTextField>
  );
};

export type FormTextFieldProps = {
  name: string;
  label?: string;
  optional?: boolean;
  changeCallback?: (e: ChangeEvent) => void;
} & Props;

export const FormTextField = ({
  name,
  disabled,
  changeCallback = () => undefined,
  label = "",
  optional = false,
  ...props
}: FormTextFieldProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
        <TextField
          disabled={disabled}
          error={!!error}
          helperText={error ? error.message : " "}
          label={optional ? `${label} (optional)` : label}
          onBlur={onBlur}
          value={(value as string) || ""}
          onChange={(e) => {
            onChange(e);
            changeCallback(e);
          }}
          {...props}
        />
      )}
    />
  );
};

export type FormHiddenTextFieldProps = {
  name: string;
  label?: string;
  changeCallback?: (e: ChangeEvent) => void;
} & PasswordProps;

export const FormHiddenTextField = ({
  name,
  disabled,
  label = "",
  changeCallback = () => undefined,
  ...props
}: FormHiddenTextFieldProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
        <HiddenTextField
          disabled={disabled}
          error={!!error}
          helperText={error ? error.message : " "}
          label={label}
          onBlur={onBlur}
          value={(value as string) || ""}
          onChange={(e) => {
            onChange(e);
            changeCallback(e);
          }}
          {...props}
        />
      )}
    />
  );
};

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>(function TextMaskCustom(props, ref) {
  const { onChange, name, ...otherProps } = props;
  return (
    <IMaskInput
      inputRef={ref}
      lazy={false}
      mask={"0000-0000-0000-0000"}
      onAccept={(value: string) => onChange({ target: { name: name, value } })}
      overwrite
      unmask={true}
      {...otherProps}
    />
  );
});

export const FormMaskedTextField = ({ sx, name, disabled, InputProps, label = "", ...props }: FormTextFieldProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value }, fieldState: { error, isDirty } }) => (
        <TextField
          disabled={disabled}
          error={!!error}
          helperText={error ? error.message : " "}
          label={label}
          onBlur={onBlur}
          onChange={onChange}
          value={(value as string) || ""}
          InputProps={{
            inputComponent: TextMaskCustom as unknown as React.ElementType<InputBaseComponentProps, keyof React.JSX.IntrinsicElements>,
            ...InputProps,
          }}
          sx={{
            "& .MuiInputBase-input": {
              color: (theme) => (isDirty ? theme.palette.text.primary : theme.palette.text.disabled),
            },
            ...sx,
          }}
          {...props}
        />
      )}
    />
  );
};
